import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface WallpaperModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  text: string;
  author: string;
}

type Format = "phone" | "desktop";
type Style = "dark" | "light" | "warm";

const FORMATS: Record<Format, { w: number; h: number; label: string }> = {
  phone:   { w: 1080, h: 1920, label: "Phone" },
  desktop: { w: 1920, h: 1080, label: "Desktop" },
};

const STYLES: Record<Style, {
  label: string;
  bg: string;
  text: string;
  author: string;
  watermark: string;
  divider: string;
}> = {
  dark: {
    label: "Dark",
    bg: "#1a1814",
    text: "#f0ead8",
    author: "#b0a898",
    watermark: "#4a453e",
    divider: "#3a352e",
  },
  light: {
    label: "Light",
    bg: "#fafaf8",
    text: "#1c1917",
    author: "#78716c",
    watermark: "#c0bab2",
    divider: "#e7e5e0",
  },
  warm: {
    label: "Warm",
    bg: "#f5e9d0",
    text: "#3d2e1a",
    author: "#8b6e4a",
    watermark: "#c4a97a",
    divider: "#e0cba8",
  },
};

function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
): string[] {
  const words = text.split(" ");
  const lines: string[] = [];
  let current = "";
  for (const word of words) {
    const test = current ? `${current} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && current) {
      lines.push(current);
      current = word;
    } else {
      current = test;
    }
  }
  if (current) lines.push(current);
  return lines;
}

function drawWallpaper(
  canvas: HTMLCanvasElement,
  text: string,
  author: string,
  format: Format,
  style: Style,
) {
  const fmt = FORMATS[format];
  const sty = STYLES[style];
  const { w, h } = fmt;

  canvas.width = w;
  canvas.height = h;

  const ctx = canvas.getContext("2d")!;

  ctx.fillStyle = sty.bg;
  ctx.fillRect(0, 0, w, h);

  const isPhone = format === "phone";
  const maxTextWidth = Math.round(w * 0.78);
  const quoteFontSize = isPhone ? 68 : 76;
  const authorFontSize = isPhone ? 46 : 52;
  const watermarkFontSize = isPhone ? 34 : 38;
  const quoteLineHeight = Math.round(quoteFontSize * 1.45);
  const authorGap = isPhone ? 64 : 72;
  const padding = isPhone ? 72 : 80;
  const centerX = w / 2;

  ctx.textAlign = "center";
  ctx.textBaseline = "top";

  ctx.font = `italic ${quoteFontSize}px Georgia, "Times New Roman", serif`;
  ctx.fillStyle = sty.text;

  const rawLines = wrapText(ctx, `\u201C${text}\u201D`, maxTextWidth);
  const quoteBlockHeight = rawLines.length * quoteLineHeight;

  const dividerH = 2;
  const dividerGap = authorGap * 0.6;
  const totalBlock =
    quoteBlockHeight + dividerGap + dividerH + dividerGap + authorFontSize;

  let y = Math.round((h - totalBlock) / 2);

  for (const line of rawLines) {
    ctx.fillText(line, centerX, y);
    y += quoteLineHeight;
  }

  y += dividerGap;
  ctx.fillStyle = sty.divider;
  const dividerW = Math.round(w * 0.12);
  ctx.fillRect(centerX - dividerW / 2, y, dividerW, dividerH);
  y += dividerH + dividerGap;

  ctx.font = `${authorFontSize}px Georgia, "Times New Roman", serif`;
  ctx.fillStyle = sty.author;
  ctx.fillText(`\u2014\u2009${author}`, centerX, y);

  ctx.font = `${watermarkFontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
  ctx.fillStyle = sty.watermark;
  ctx.textAlign = "right";
  ctx.textBaseline = "bottom";
  ctx.fillText("QuotedsArrow", w - padding, h - padding);
}

export function WallpaperModal({ open, onOpenChange, text, author }: WallpaperModalProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [format, setFormat] = useState<Format>("phone");
  const [style, setStyle] = useState<Style>("dark");
  const [isSharing, setIsSharing] = useState(false);

  useEffect(() => {
    if (!open || !canvasRef.current) return;
    drawWallpaper(canvasRef.current, text, author, format, style);
  }, [open, text, author, format, style]);

  const fmt = FORMATS[format];
  // Cap preview width so the desktop (16:9) preview doesn't overflow the
  // modal on narrow viewports. The Dialog content is roughly `max-w-xl` minus
  // padding; on 375px viewports the inner space is ~295px.
  const [containerW, setContainerW] = useState<number>(440);
  useEffect(() => {
    function update() {
      // Subtract dialog padding (p-6 = 48px) and a small safety margin.
      const avail = Math.min(window.innerWidth - 32, 576) - 48;
      setContainerW(Math.max(180, avail));
    }
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);
  const PREVIEW_W = Math.min(format === "phone" ? 210 : 440, containerW);
  const PREVIEW_H = Math.round((PREVIEW_W / fmt.w) * fmt.h);

  function handleDownload() {
    if (!canvasRef.current) return;
    const url = canvasRef.current.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = `quote-wallpaper-${format}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  async function handleShareImage() {
    if (!canvasRef.current) return;
    setIsSharing(true);
    try {
      const blob = await new Promise<Blob | null>((resolve) =>
        canvasRef.current!.toBlob(resolve, "image/png"),
      );
      if (!blob) return;
      const file = new File([blob], `quote-wallpaper-${format}.png`, { type: "image/png" });
      if (!navigator.canShare?.({ files: [file] })) return;
      await navigator.share({ files: [file] });
    } catch (err) {
      // AbortError = user dismissed the sheet — not an error worth surfacing
      if (err instanceof Error && err.name !== "AbortError") {
        console.error("Image share failed:", err);
      }
    } finally {
      setIsSharing(false);
    }
  }

  // True only on devices / browsers that support file sharing (mobile Safari/Chrome).
  // Evaluated lazily so SSR/desktop never sees the button.
  const canShareImage =
    typeof navigator !== "undefined" &&
    typeof navigator.canShare === "function" &&
    (() => {
      try {
        return navigator.canShare({
          files: [new File([], "test.png", { type: "image/png" })],
        });
      } catch {
        return false;
      }
    })();

  const pillBase =
    "px-3 py-1.5 rounded-full text-xs font-medium border transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-1";
  const pillActive = "bg-stone-900 text-white border-stone-900";
  const pillInactive =
    "bg-white text-stone-500 border-stone-200 hover:border-stone-300 hover:text-stone-700";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl w-full p-6">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold text-stone-900">
            Save as wallpaper
          </DialogTitle>
        </DialogHeader>

        <div className="mt-3 flex flex-col items-center gap-5">
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
            <div className="flex items-center gap-1.5">
              {(Object.keys(FORMATS) as Format[]).map((f) => (
                <button
                  key={f}
                  onClick={() => setFormat(f)}
                  className={`${pillBase} ${format === f ? pillActive : pillInactive}`}
                >
                  {FORMATS[f].label}
                </button>
              ))}
            </div>

            <span className="hidden sm:inline text-stone-300 select-none">|</span>

            <div className="flex items-center gap-1.5">
              {(Object.keys(STYLES) as Style[]).map((s) => (
                <button
                  key={s}
                  onClick={() => setStyle(s)}
                  className={`${pillBase} ${style === s ? pillActive : pillInactive}`}
                >
                  {STYLES[s].label}
                </button>
              ))}
            </div>
          </div>

          <div
            className="overflow-hidden rounded-2xl shadow-lg border border-stone-200 shrink-0"
            style={{ width: PREVIEW_W, height: PREVIEW_H }}
          >
            <canvas
              ref={canvasRef}
              style={{ width: PREVIEW_W, height: PREVIEW_H, display: "block" }}
            />
          </div>

          <p className="text-xs text-stone-400 -mt-2">
            {fmt.w} × {fmt.h} px &middot; PNG
          </p>

          {canShareImage && (
            <button
              onClick={handleShareImage}
              disabled={isSharing}
              className="w-full rounded-full border border-stone-200 bg-white py-2.5 text-sm font-semibold text-stone-700 transition hover:bg-stone-50 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-2 disabled:opacity-60 disabled:active:scale-100"
            >
              {isSharing ? "Preparing…" : "↗ Share image"}
            </button>
          )}

          <button
            onClick={handleDownload}
            className="w-full rounded-full bg-stone-900 py-2.5 text-sm font-semibold text-white transition hover:bg-stone-800 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-2"
          >
            Download
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
